import { StrictMode } from 'react';
import { Helmet } from 'react-helmet-async';
import { SWRConfig } from 'swr';
import { Config, ConfigContext } from '../../config';
import { LocalizationContext, Translations } from '../../i18n/i18n';
import { PreloadedData } from '../../state/api';
import { CategoryTreeContainer } from '../../state/categories';
import { FavoritesContainer } from '../../state/favorites';
import { FilterContainer } from '../../state/filters';
import { MessagesContainer } from '../../state/messages';
import { MetaContainer } from '../../state/meta';
import { ProductContainer } from '../../state/product';
import { UiContainer } from '../../state/ui';
import { Cart } from '../Cart/Cart';
import { ConsentMode } from '../ConsentMode/ConsentMode';
import { ErrorBoundary } from '../ErrorBoundary';
import { FlyToCartContainer } from '../FlyToCart/FlyToCart';
import { Footer } from '../Footer/Footer';
import '../global.css';
import { Header } from '../Header/Header';
import { TopBanner } from '../Header/TopBanner/TopBanner';
import { Routes } from '../Routes';
import '../variables.css';
import './App.css';
import { CustomerTypeModal } from './CustomerTypeModal/CustomerTypeModal';
import { NetworkErrorHandling } from './NetworkErrorHandling';
import { ScrollPreventer } from './ScrollPreventer';

function StateContainers({
	children,
	config,
	localization,
	preloadedData,
}: {
	children: JSX.Element;
	config: Config;
	localization: Translations;
	preloadedData: PreloadedData;
}) {
	return (
		<MetaContainer.Provider>
			<SWRConfig value={{ fallback: preloadedData }}>
				<ConfigContext.Provider value={config}>
					<LocalizationContext.Provider value={localization}>
						<UiContainer.Provider>
							<MessagesContainer.Provider>
								<ProductContainer.Provider>
									<FilterContainer.Provider>
										<CategoryTreeContainer.Provider>
											<FavoritesContainer.Provider>
												<FlyToCartContainer.Provider>{children}</FlyToCartContainer.Provider>
											</FavoritesContainer.Provider>
										</CategoryTreeContainer.Provider>
									</FilterContainer.Provider>
								</ProductContainer.Provider>
							</MessagesContainer.Provider>
						</UiContainer.Provider>
					</LocalizationContext.Provider>
				</ConfigContext.Provider>
			</SWRConfig>
		</MetaContainer.Provider>
	);
}

type AppProps = {
	config: Config;
	preloadedData: PreloadedData;
	localization: Translations;
};

export const App = ({ config, preloadedData, localization }: AppProps): JSX.Element => {
	return (
		<StrictMode>
			<StateContainers config={config} localization={localization} preloadedData={preloadedData}>
				<ErrorBoundary>
					<ConsentMode />
					<NetworkErrorHandling />
					<Helmet>
						<title>{localization.title}</title>
					</Helmet>
					<div className="not-mobile">
						<TopBanner />
					</div>
					<ScrollPreventer />
					<div id="app">
						{/* needs restyling. should be used later */}
						{/* <Favorites /> */}
						<Cart />
						<Header />
						<Routes />
					</div>
					<Footer />
					<CustomerTypeModal />
				</ErrorBoundary>
			</StateContainers>
		</StrictMode>
	);
};
